import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getCourses,
    createCourse,
    deleteCourse,
    getStatistics,
    signup,
    getEnrollmentStats,
    unassignAdminStudentCourse
} from '../../services/api';
import ManageCourse from './managecourse.js';
import { useAdmin } from '../../context/AdminContext';


function Admin() {
    const { selectedDepartment, setSelectedDepartment, searchTerm, setSearchTerm } = useAdmin();

    const navigate = useNavigate();
    const [coursesData, setCoursesData] = useState([]);
    const [showEditPage, setShowEditPage] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [showAddCoursePopup, setShowAddCoursePopup] = useState(false);
    const [newCourseTitle, setNewCourseTitle] = useState('');
    const [newCourseDepartment, setNewCourseDepartment] = useState('');
    const [newCourseInstructor, setNewCourseInstructor] = useState('Admin');

    const [showAnnouncementPopup, setShowAnnouncementPopup] = useState(false);
    const [announcementTitle, setAnnouncementTitle] = useState('');
    const [announcementMessage, setAnnouncementMessage] = useState('');

    const [showAddStudentPopup, setShowAddStudentPopup] = useState(false);
    const [studentName, setStudentName] = useState('');
    const [studentEmail, setStudentEmail] = useState('');
    const [studentPassword, setStudentPassword] = useState('');
    const [stats, setStats] = useState({
        totalCourses: 0,
        totalStudents: 0,
        activeStudents: 0,
        inactiveStudents: 0,
        newStudentsMonth: 0,
        totalEnrollments: 0,
        totalRevenue: 0,
        trends: {
            students: '0%',
            courses: '0%',
            enrollments: '0%',
            revenue: '0%'
        }
    });
    const [enrollmentStats, setEnrollmentStats] = useState([]);
    const [showEnrollmentDetails, setShowEnrollmentDetails] = useState(null);
    const [enrollmentToUnassign, setEnrollmentToUnassign] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [courseToDelete, setCourseToDelete] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
    };

    // Function to load courses from API
    const loadCourses = async () => {
        try {
            console.log('Loading courses from API...');
            const coursesResponse = await getCourses(true);
            if (coursesResponse.success) {
                console.log('Courses loaded:', coursesResponse.data);
                setCoursesData(coursesResponse.data);
            }

            // Load statistics
            const statsResponse = await getStatistics();
            if (statsResponse.success) {
                setStats({
                    ...statsResponse.data,
                    totalRevenue: statsResponse.data.totalRevenue || 0
                });
            }

            // Load enrollment stats
            const enrollmentResponse = await getEnrollmentStats();
            if (enrollmentResponse.success) {
                setEnrollmentStats(enrollmentResponse.data);
            }
        } catch (error) {
            console.error('Failed to load courses or statistics:', error);
        }
    };

    useEffect(() => {
        // Load courses on component mount
        loadCourses();

        // Check for department query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const departmentParam = urlParams.get('department');
        if (departmentParam) {
            const decodedDepartment = decodeURIComponent(departmentParam);
            setSelectedDepartment(decodedDepartment);
        }
    }, []);

    // Listen for course updates
    useEffect(() => {
        const handleCoursesUpdate = (event) => {
            console.log('Admin: Courses updated:', event.detail);
            loadCourses(); // Reload courses when updated
        };

        window.addEventListener('coursesUpdated', handleCoursesUpdate);
        return () => window.removeEventListener('coursesUpdated', handleCoursesUpdate);
    }, []);

    const adjustColor = (color, percent) => {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        const R = (num >> 16) + amt;
        const G = (num >> 8 & 0x00FF) + amt;
        const B = (num & 0x0000FF) + amt;
        return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
            (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
            (B < 255 ? B < 1 ? 0 : B : 255))
            .toString(16).slice(1);
    };

    // Filter courses based on department and search
    const filteredCourses = coursesData.filter(course => {
        const matchesDepartment = selectedDepartment === 'all' ||
            (course.department && typeof course.department === 'string' && course.department.toLowerCase() === selectedDepartment.toLowerCase());
        const matchesSearch = (course.title && typeof course.title === 'string' && course.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (course.department && typeof course.department === 'string' && course.department.toLowerCase().includes(searchTerm.toLowerCase()))
        return matchesDepartment && matchesSearch;
    });


    const handleBackToDashboard = () => {
        setShowEditPage(false);
        setSelectedCourseId(null);
        // Refresh courses data after editing
        loadCourses();
    };


    const handleEditCourse = (courseId) => {
        navigate(`/admin/managecourse/${courseId}`);
    };

    const handleAddCourseClick = () => {
        setShowAddCoursePopup(true);
    };

    const handleAddCourseSubmit = async (e) => {
        e.preventDefault();
        if (newCourseTitle.trim() && newCourseDepartment.trim()) {
            try {
                const result = await createCourse({
                    title: newCourseTitle.trim(),
                    department: newCourseDepartment.trim(),
                    instructor: newCourseInstructor.trim() || 'Admin',
                    students: 0,
                    status: 'Active',
                    difficulty: 'Beginner',
                    icon: 'fa-book',
                    color: '#3498db',
                    image: 'https://picsum.photos/300/200',
                    price: 499,
                    originalPrice: 3999,
                    discount: 87,
                    rating: 0,
                    ratingCount: 0,
                    duration: '0 hours',
                    lectures: 0,
                    level: 'Beginner',
                    lastUpdated: new Date().toLocaleDateString(),
                    articles: 0,
                    resources: 0,
                    recommended: false,
                    description: 'New course description',
                    fullDescription: 'New course full description',
                    whatYouLearn: [],
                    requirements: [],
                    skills: [],
                    curriculum: [],
                    instructorDetails: {
                        name: newCourseInstructor.trim() || 'Admin',
                        title: 'Administrator',
                        avatar: '',
                        rating: 0,
                        students: 0,
                        courses: 0,
                        bio: ''
                    }
                });

                if (result.success) {
                    // Refresh courses data
                    loadCourses();

                    setNewCourseTitle('');
                    setNewCourseDepartment('');
                    setNewCourseInstructor('Admin');
                    setShowAddCoursePopup(false);
                    showToast('Course added successfully!', 'success');
                } else {
                    showToast('Failed to add course: ' + result.message, 'error');
                }
            } catch (error) {
                showToast('An error occurred while adding the course: ' + error.message, 'error');
            }
        }
    };

    const handleClosePopup = () => {
        setShowAddCoursePopup(false);
        setNewCourseTitle('');
        setNewCourseDepartment('');
        setNewCourseInstructor('Admin');
    };

    const handleAnnouncementClick = () => {
        setShowAnnouncementPopup(true);
    };

    const handleAnnouncementSubmit = (e) => {
        e.preventDefault();
        // Since we removed data.js, we log it or handle via API if implemented later.
        if (announcementTitle.trim() && announcementMessage.trim()) {
            setAnnouncementTitle('');
            setAnnouncementMessage('');
            setShowAnnouncementPopup(false);
            showToast('Announcement sent successfully!', 'success');
        }
    };

    const handleCloseAnnouncementPopup = () => {
        setShowAnnouncementPopup(false);
        setAnnouncementTitle('');
        setAnnouncementMessage('');
    };

    const handleAddStudentClick = () => {
        setShowAddStudentPopup(true);
    };

    const handleAddStudentSubmit = async (e) => {
        e.preventDefault();
        if (studentName.trim() && studentEmail.trim() && studentPassword.trim()) {
            try {
                const response = await signup({
                    name: studentName.trim(),
                    email: studentEmail.trim(),
                    password: studentPassword.trim(),
                    role: 'student'
                });

                if (response.success) {
                    showToast('Student added successfully!', 'success');
                    setShowAddStudentPopup(false);
                    setStudentName('');
                    setStudentEmail('');
                    setStudentPassword('');
                    // Reload stats to reflect new student
                    loadCourses();
                } else {
                    showToast('Failed to add student: ' + response.message, 'error');
                }
            } catch (error) {
                showToast('An error occurred while adding student: ' + error.message, 'error');
            }
        }
    };

    const handleCloseStudentPopup = () => {
        setShowAddStudentPopup(false);
        setStudentName('');
        setStudentEmail('');
        setStudentPassword('');
    };

    const handleDeleteCourseClick = (courseId) => {
        setCourseToDelete(courseId);
        setShowDeleteModal(true);
    };

    const confirmDeleteCourse = async () => {
        try {
            const result = await deleteCourse(courseToDelete);
            if (result.success) {
                // Refresh courses data
                loadCourses();
                showToast('Course deleted successfully!', 'success');
                setShowDeleteModal(false);
                setCourseToDelete(null);
            } else {
                showToast('Failed to delete course: ' + result.message, 'error');
            }
        } catch (error) {
            showToast('An error occurred while deleting the course: ' + error.message, 'error');
        }
    };

    const handleUnassignEnrollment = async () => {
        if (!enrollmentToUnassign) return;
        const { studentId, courseId } = enrollmentToUnassign;
        setEnrollmentToUnassign(null);
        try {
            const res = await unassignAdminStudentCourse(studentId, courseId);
            if (res.success) {
                showToast(res.message || 'Student unassigned successfully', 'success');
                // Reload dashboard stats and enrollments
                loadCourses();
            } else {
                showToast(res.message || 'Failed to unassign student', 'error');
            }
        } catch (error) {
            console.error(error);
            showToast(error.message || 'Error unassigning student', 'error');
        }
    };




    return (
        <div className="p-8 max-md:p-4">
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />

            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-6 mb-8">
                {[
                    { icon: 'fa-users', value: stats.totalStudents, label: 'Total Students', gradient: 'from-[#667eea] to-[#764ba2]', trend: stats.trends?.students || '0%' },
                    { icon: 'fa-user-check', value: stats.activeStudents, label: 'Active Students', gradient: 'from-[#f093fb] to-[#f5576c]', trend: '8%' },
                    { icon: 'fa-user-xmark', value: stats.inactiveStudents, label: 'Inactive Students', gradient: 'from-[#4facfe] to-[#00f2fe]', trend: '-3%' },
                    { icon: 'fa-user-plus', value: stats.newStudentsMonth, label: 'New This Month', gradient: 'from-[#43e97b] to-[#38f9d7]', trend: '15%' },
                    { icon: 'fa-indian-rupee-sign', value: `₹${Number(stats.totalRevenue).toLocaleString()}`, label: 'Total Profit', gradient: 'from-[#00b26f] to-[#007bff]', trend: stats.trends?.revenue || '23%' }
                ].map((stat, idx) => (
                    <div key={idx} className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-md transition-all relative overflow-hidden before:content-[''] before:absolute before:top-0 before:left-0 before:w-full before:h-1 before:bg-gradient-to-r before:from-[#00b26f] before:to-[#007bff] hover:-translate-y-2 hover:shadow-xl">
                        <div className="flex flex-col sm:flex-row justify-between items-start mb-2 sm:mb-4 gap-2 sm:gap-0">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-xl sm:text-2xl text-white bg-gradient-to-br shrink-0 ${stat.gradient}`}>
                                <i className={`fa-solid ${stat.icon}`}></i>
                            </div>
                            <div className={`flex items-center gap-1 text-[10px] sm:text-sm px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md font-semibold self-start sm:self-auto ${stat.trend.startsWith('-') ? 'bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900 text-green-600 dark:text-green-400'}`}>
                                <i className={`fa-solid fa-arrow-${stat.trend.startsWith('-') ? 'down' : 'up'}`}></i>
                                {stat.trend}
                            </div>
                        </div>
                        <div className="text-xl sm:text-2xl md:text-3xl font-bold mb-0.5 sm:mb-1 text-gray-800 dark:text-gray-100 truncate">{stat.value}</div>
                        <div className="text-gray-500 dark:text-gray-400 text-[10px] sm:text-sm font-medium leading-tight">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                    { icon: 'fa-plus', text: 'Add New Course', onClick: handleAddCourseClick },
                    { icon: 'fa-user-plus', text: 'Add Student', onClick: handleAddStudentClick },
                    { icon: 'fa-file-export', text: 'Export Data' },
                    { icon: 'fa-envelope', text: 'Send Announcements', onClick: handleAnnouncementClick }
                ].map((action, idx) => (
                    <button
                        key={idx}
                        onClick={action.onClick}
                        className="p-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl cursor-pointer text-sm md:text-base font-semibold transition-all flex flex-col md:flex-row items-center justify-center md:justify-start gap-2.5 hover:bg-[#00b26f] hover:text-white hover:border-[#00b26f] hover:-translate-y-1 text-gray-700 dark:text-gray-300"
                    >
                        <i className={`fa-solid ${action.icon} text-xl`}></i>
                        <span className="text-center md:text-left">{action.text}</span>
                    </button>
                ))}
            </div>

            {/* Course Management - Always show this section */}
            <div className="bg-white dark:bg-gray-800 p-4 md:p-8 rounded-2xl shadow-md border border-gray-100 dark:border-gray-700">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                        <i className="fa-solid fa-graduation-cap text-[#00b26f]"></i>
                        Manage Courses
                        {coursesData.length > 0 && <span className="text-xs md:text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-gray-600 dark:text-gray-400">({coursesData.length})</span>}
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                        <div className="relative flex-1 sm:w-64">
                            <i className="fa-solid fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                            <input
                                type="text"
                                placeholder="Search courses..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-100 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium text-sm transition-all focus:border-[#00b26f] focus:bg-white dark:focus:bg-gray-700 outline-none"
                            />
                        </div>
                        <select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            className="w-full sm:w-56 px-4 py-2.5 border-2 border-gray-100 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium text-sm transition-all focus:border-[#00b26f] outline-none"
                        >
                            <option value="all">All Departments</option>
                            <option value="IT & ECE">IT & ECE</option>
                            <option value="Mechanical Engineering">Mechanical Engineering</option>
                            <option value="Civil Engineering">Civil Engineering</option>
                            <option value="Electrical Vehicle">Electrical Vehicle</option>
                            <option value="Non-Destructive Testing">Non-Destructive Testing</option>
                            <option value="Destructive Testing">Destructive Testing</option>
                        </select>
                        <button
                            onClick={() => setSelectedDepartment('all')}
                            className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-[#00b26f] hover:text-white transition-all text-sm whitespace-nowrap"
                        >
                            Show All
                        </button>
                    </div>
                </div>

                {/* Branchwise Quick Filters */}
                <div className="flex flex-wrap gap-2 mb-8 pb-4 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 w-full mb-1">Quick Filters:</span>
                    {['all', 'IT & ECE', 'Mechanical Engineering', 'Civil Engineering', 'Electrical Vehicle', 'Non-Destructive Testing', 'Destructive Testing'].map((dept) => (
                        <button
                            key={dept}
                            onClick={() => setSelectedDepartment(dept)}
                            className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${selectedDepartment === dept
                                ? 'bg-[#00b26f] text-white shadow-lg scale-105'
                                : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                        >
                            {dept === 'all' ? 'All' : dept}
                        </button>
                    ))}
                </div>

                {/* Add Course Popup */}
                {showAddCoursePopup && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex justify-center items-center p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-[fadeIn_0.3s_ease-out]">
                            <div className="bg-[#00b26f] text-white p-5 flex justify-between items-center">
                                <h2 className="text-xl font-bold m-0 flex items-center gap-2">
                                    <i className="fa-solid fa-plus-circle"></i> Add New Course
                                </h2>
                                <button onClick={handleClosePopup} className="bg-transparent border-none text-white text-xl cursor-pointer hover:text-gray-200 transition-colors">
                                    <i className="fa-solid fa-times"></i>
                                </button>
                            </div>
                            <form onSubmit={handleAddCourseSubmit} className="p-6">
                                <div className="mb-5">
                                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2 text-sm">Course Title</label>
                                    <input
                                        type="text"
                                        value={newCourseTitle}
                                        onChange={(e) => setNewCourseTitle(e.target.value)}
                                        placeholder="Enter course title"
                                        required
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors focus:border-[#00b26f] focus:outline-none"
                                    />
                                </div>
                                <div className="mb-5">
                                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2 text-sm">Instructor Name</label>
                                    <input
                                        type="text"
                                        value={newCourseInstructor}
                                        onChange={(e) => setNewCourseInstructor(e.target.value)}
                                        placeholder="Enter instructor name"
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors focus:border-[#00b26f] focus:outline-none"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2 text-sm">Department</label>
                                    <select
                                        value={newCourseDepartment}
                                        onChange={(e) => setNewCourseDepartment(e.target.value)}
                                        required
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors focus:border-[#00b26f] focus:outline-none"
                                    >
                                        <option value="" disabled>Select Department</option>
                                        <option value="IT & ECE">IT & ECE</option>
                                        <option value="Mechanical Engineering">Mechanical Engineering</option>
                                        <option value="Civil Engineering">Civil Engineering</option>
                                        <option value="Electrical Vehicle">Electrical Vehicle</option>
                                        <option value="Non-Destructive Testing">Non-Destructive Testing</option>
                                        <option value="Destructive Testing">Destructive Testing</option>
                                    </select>
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <button type="button" onClick={handleClosePopup} className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                        Cancel
                                    </button>
                                    <button type="submit" className="px-5 py-2.5 bg-[#00b26f] text-white font-semibold rounded-lg hover:bg-[#008f5a] transition-colors shadow-md hover:shadow-lg">
                                        Create Course
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Add Student Popup */}
                {showAddStudentPopup && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex justify-center items-center p-4">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-[fadeIn_0.3s_ease-out]">
                            <div className="bg-[#00b26f] text-white p-5 flex justify-between items-center">
                                <h2 className="text-xl font-bold m-0 flex items-center gap-2">
                                    <i className="fa-solid fa-user-plus"></i> Add New Student
                                </h2>
                                <button onClick={handleCloseStudentPopup} className="bg-transparent border-none text-white text-xl cursor-pointer hover:text-gray-200 transition-colors">
                                    <i className="fa-solid fa-times"></i>
                                </button>
                            </div>
                            <form onSubmit={handleAddStudentSubmit} className="p-6">
                                <div className="mb-4">
                                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2 text-sm">Full Name</label>
                                    <input
                                        type="text"
                                        value={studentName}
                                        onChange={(e) => setStudentName(e.target.value)}
                                        placeholder="Enter full name"
                                        required
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors focus:border-[#00b26f] focus:outline-none"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2 text-sm">Email Address</label>
                                    <input
                                        type="email"
                                        value={studentEmail}
                                        onChange={(e) => setStudentEmail(e.target.value)}
                                        placeholder="Enter email address"
                                        required
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors focus:border-[#00b26f] focus:outline-none"
                                    />
                                </div>
                                <div className="mb-6">
                                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2 text-sm">Temporary Password</label>
                                    <input
                                        type="password"
                                        value={studentPassword}
                                        onChange={(e) => setStudentPassword(e.target.value)}
                                        placeholder="Set temporary password"
                                        required
                                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors focus:border-[#00b26f] focus:outline-none"
                                    />
                                </div>
                                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <button type="button" onClick={handleCloseStudentPopup} className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                        Cancel
                                    </button>
                                    <button type="submit" className="px-5 py-2.5 bg-[#00b26f] text-white font-semibold rounded-lg hover:bg-[#008f5a] transition-colors shadow-md hover:shadow-lg">
                                        Create Student
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Show courses or empty state */}
                {filteredCourses.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredCourses.map((course) => (
                            <div
                                key={course.id}
                                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 border-gray-200 dark:border-gray-700 hover:border-[#00b26f] group relative w-full"
                            >
                                {/* Top Color Bar */}
                                <div
                                    className="h-2 w-full"
                                    style={{ background: `linear-gradient(90deg, ${course.color || '#3498db'} 0%, ${adjustColor(course.color || '#3498db', -20)} 100%)` }}
                                ></div>

                                {/* Card Content */}
                                <div className="p-6">
                                    {/* Icon and Action Buttons */}
                                    <div className="flex justify-between items-start mb-4">
                                        <div
                                            className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl text-white shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300"
                                            style={{ background: `linear-gradient(135deg, ${course.color || '#3498db'} 0%, ${adjustColor(course.color || '#3498db', -20)} 100%)` }}
                                        >
                                            <i className={`fa-solid ${course.icon || 'fa-book'}`}></i>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEditCourse(course.id)}
                                                className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                title="Edit Course"
                                            >
                                                <i className="fa-solid fa-edit text-sm"></i>
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCourseClick(course.id)}
                                                className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                                title="Delete Course"
                                            >
                                                <i className="fa-solid fa-trash text-sm"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Course Title */}
                                    <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100 mb-2 group-hover:text-[#00b26f] transition-colors min-h-[3.5rem] break-words">
                                        {course.title}
                                    </h3>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200 dark:border-gray-700 mb-4"></div>

                                    {/* Course Details */}
                                    <div className="space-y-3 mb-4">
                                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                            <i className="fa-solid fa-building text-[#00b26f] w-4 flex-shrink-0"></i>
                                            <span className="font-medium break-words">{course.department}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                            <i className="fa-solid fa-chalkboard-user text-blue-500 w-4 flex-shrink-0"></i>
                                            <span className="break-words">{course.instructor}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                                            <i className="fa-solid fa-users text-purple-500 w-4 flex-shrink-0"></i>
                                            <span className="font-semibold">{course.students}</span>
                                            <span className="text-xs whitespace-nowrap">students</span>
                                        </div>
                                    </div>

                                    {/* Difficulty Badge */}
                                    <div className="mb-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${course.difficulty === 'Beginner' ? 'bg-gradient-to-r from-[#28a745] to-[#20c997]' : course.difficulty === 'Intermediate' ? 'bg-gradient-to-r from-[#ffc107] to-[#fd7e14]' : 'bg-gradient-to-r from-[#dc3545] to-[#e83e8c]'}`}>
                                            {course.difficulty || 'Beginner'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="max-w-md mx-auto">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fas fa-book text-gray-400 text-3xl"></i>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">No Courses Found</h3>
                            <p className="text-gray-600 dark:text-gray-300 mb-6">
                                {searchTerm ? `No courses found matching "${searchTerm}"` : 'No courses available in this department'}
                            </p>
                            {!searchTerm && (
                                <button
                                    onClick={handleAddCourseClick}
                                    className="bg-[#00b26f] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#008f5a] transition-colors inline-flex items-center gap-2"
                                >
                                    <i className="fas fa-plus"></i>
                                    Add First Course
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Add Course Popup */}
            {showAddCoursePopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Add New Course</h2>
                        <form onSubmit={handleAddCourseSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Course Title</label>
                                <input
                                    type="text"
                                    value={newCourseTitle}
                                    onChange={(e) => setNewCourseTitle(e.target.value)}
                                    placeholder="Enter course title"
                                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Instructor Name</label>
                                <input
                                    type="text"
                                    value={newCourseInstructor}
                                    onChange={(e) => setNewCourseInstructor(e.target.value)}
                                    placeholder="Enter instructor name"
                                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Department</label>
                                <select
                                    value={newCourseDepartment}
                                    onChange={(e) => setNewCourseDepartment(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                                    required
                                >
                                    <option value="">Select department</option>
                                    <option value="IT & ECE">IT & ECE</option>
                                    <option value="Mechanical Engineering">Mechanical Engineering</option>
                                    <option value="Civil Engineering">Civil Engineering</option>
                                    <option value="Electrical Vehicle">Electrical Vehicle</option>
                                    <option value="Non-Destructive Testing">Non-Destructive Testing</option>
                                    <option value="Destructive Testing">Destructive Testing</option>
                                </select>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 bg-[#00b26f] text-white border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5"
                                >
                                    Add Course
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClosePopup}
                                    className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Announcement Popup */}
            {showAnnouncementPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Send Announcement</h2>
                        <form onSubmit={handleAnnouncementSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Announcement Title</label>
                                <input
                                    type="text"
                                    value={announcementTitle}
                                    onChange={(e) => setAnnouncementTitle(e.target.value)}
                                    placeholder="Enter announcement title"
                                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)]"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Message</label>
                                <textarea
                                    value={announcementMessage}
                                    onChange={(e) => setAnnouncementMessage(e.target.value)}
                                    placeholder="Enter announcement message"
                                    rows="4"
                                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-[#00b26f] focus:shadow-[0_0_0_3px_rgba(0,178,111,0.1)] resize-none"
                                    required
                                />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 bg-[#00b26f] text-white border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-[#008f5a] hover:-translate-y-0.5"
                                >
                                    Send Announcement
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCloseAnnouncementPopup}
                                    className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 border-none py-3 rounded-lg font-semibold cursor-pointer transition-all hover:bg-gray-300 dark:hover:bg-gray-500 hover:-translate-y-0.5"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Enrollment Statistics Section */}
            <div className="bg-white dark:bg-gray-800 p-8 max-md:p-4 rounded-2xl shadow-md mt-8">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
                    <i className="fa-solid fa-graduation-cap text-[#00b26f]"></i>
                    Enrollment Analytics
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b-2 border-gray-100 dark:border-gray-700">
                                <th className="py-4 px-4 font-bold text-gray-600 dark:text-gray-400">Course Title</th>
                                <th className="py-4 px-4 font-bold text-gray-600 dark:text-gray-400">Total Purchases</th>
                                <th className="py-4 px-4 font-bold text-gray-600 dark:text-gray-400">Students</th>
                                <th className="py-4 px-4 font-bold text-gray-600 dark:text-gray-400">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {enrollmentStats.map((course) => (
                                <React.Fragment key={course.id}>
                                    <tr className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="py-4 px-4 font-semibold text-gray-800 dark:text-gray-200">{course.title}</td>
                                        <td className="py-4 px-4">
                                            <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full font-bold">
                                                {course.purchase_count}
                                            </span>
                                        </td>
                                        <td className="py-4 px-4 text-gray-600 dark:text-gray-400 text-sm">
                                            {course.students.length > 0 ? (
                                                <div className="flex -space-x-2">
                                                    {course.students.slice(0, 3).map((s, idx) => (
                                                        <div key={idx} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00b26f] to-[#007bff] border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-[10px] font-bold">
                                                            {s.name.charAt(0)}
                                                        </div>
                                                    ))}
                                                    {course.students.length > 3 && (
                                                        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 border-2 border-white dark:border-gray-800 flex items-center justify-center text-gray-600 dark:text-gray-400 text-[10px] font-bold">
                                                            +{course.students.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                "No enrollments"
                                            )}
                                        </td>
                                        <td className="py-4 px-4">
                                            <button
                                                onClick={() => setShowEnrollmentDetails(showEnrollmentDetails === course.id ? null : course.id)}
                                                className="text-[#00b26f] hover:underline font-semibold flex items-center gap-1"
                                            >
                                                {showEnrollmentDetails === course.id ? 'Hide List' : 'View List'}
                                                <i className={`fa-solid fa-chevron-${showEnrollmentDetails === course.id ? 'up' : 'down'} text-xs`}></i>
                                            </button>
                                        </td>
                                    </tr>
                                    {showEnrollmentDetails === course.id && (
                                        <tr>
                                            <td colSpan="4" className="py-4 px-8 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    {course.students.map((student, sIdx) => (
                                                        <div key={sIdx} className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 group/item">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold">
                                                                    {student.name.charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <div className="font-bold text-gray-800 dark:text-gray-200">{student.name}</div>
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{student.email}</div>
                                                                    <div className="text-[10px] text-gray-400 mt-1">Enrolled: {new Date(student.enrolled_at).toLocaleDateString()}</div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setEnrollmentToUnassign({ 
                                                                    studentId: student.user_id, 
                                                                    studentName: student.name, 
                                                                    courseId: course.id, 
                                                                    courseTitle: course.title 
                                                                })}
                                                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors flex items-center justify-center"
                                                                title="Unassign Student from Course"
                                                            >
                                                                <i className="fa-solid fa-trash-can text-sm"></i>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex justify-center items-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-[fadeIn_0.3s_ease-out]">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fa-solid fa-trash-can text-3xl text-red-500"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Delete Course?</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-8">
                                This action cannot be undone. Are you sure you want to permanently delete this course?
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setCourseToDelete(null);
                                    }}
                                    className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteCourse}
                                    className="flex-1 px-6 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 shadow-lg shadow-red-500/30 transition-all"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Unassign Enrollment Confirmation Modal */}
            {enrollmentToUnassign && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex justify-center items-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl animate-[fadeIn_0.3s_ease-out]">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-amber-50 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fa-solid fa-circle-exclamation text-3xl text-amber-500"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Unassign Student?</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-8">
                                Are you sure you want to unassign <strong>{enrollmentToUnassign.studentName}</strong> from the course <strong>{enrollmentToUnassign.courseTitle}</strong>? This will remove their access and update the revenue statistics.
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setEnrollmentToUnassign(null)}
                                    className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUnassignEnrollment}
                                    className="flex-1 px-6 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 shadow-lg shadow-amber-500/30 transition-all"
                                >
                                    Yes, Unassign
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast.show && (
                <div className="fixed bottom-6 right-6 z-[9999] animate-[slideIn_0.3s_ease-out]">
                    <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl border-l-4 min-w-[300px] ${toast.type === 'success'
                        ? 'bg-white border-[#00b26f]'
                        : 'bg-white border-[#dc3545]'
                        }`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${toast.type === 'success' ? 'bg-emerald-50 text-[#00b26f]' : 'bg-red-50 text-[#dc3545]'
                            }`}>
                            <i className={`fa-solid ${toast.type === 'success' ? 'fa-check-circle' : 'fa-circle-exclamation'} text-lg`}></i>
                        </div>
                        <div className="flex-1">
                            <h4 className={`text-sm font-bold ${toast.type === 'success' ? 'text-[#00b26f]' : 'text-[#dc3545]'}`}>
                                {toast.type === 'success' ? 'Success' : 'Error'}
                            </h4>
                            <p className="text-[#333] text-xs font-medium">{toast.message}</p>
                        </div>
                        <button
                            onClick={() => setToast({ ...toast, show: false })}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <i className="fa-solid fa-times"></i>
                        </button>
                    </div>
                </div>
            )}

            <style jsx="true">{`
                @keyframes slideIn {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
}

export default Admin;
